import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import * as Sentry from '@sentry/react';
import {
    getPhaseFromHash,
    getLeadExtraData,
    getStorageValue,
    getUrlParamObj,
    sendOneSignalLeadCount,
    setMultipleStorageValues,
    setStorageValue,
} from 'App/Helpers';
import { useCustomDetectBot, usePageLoad } from 'App/hooks';
import { ConsentContext, LanguageContext } from 'App/Providers/games';
import { captureCustomException } from 'App/Services/ErrorTracking/captureCustomException';

import lpVideo from '../assets/video/landing_en.mp4';
import pushVideo from '../assets/video/push_en.mp4';
import pwaVideo from '../assets/video/pwa_en.mp4';
import { adDefaultMap, gaCustomDimensions, STORAGE_KEY } from '../configs';
import { createLpConfigs } from '../helpers';

const phaseMap = {
    1: 'landing',
    2: 'push',
    3: 'pwa',
    4: 'game',
};

const AppContext = createContext(null);

/**
 * Application data provider: initializes campaign/service/config state and exposes it via context.
 *
 * @param {object} props - Component props.
 * @param {object} props.appConfig - Build and campaign settings of the landing page (push campaign, Sharpstar API url).
 * @param {import("react").ReactNode} props.children - Landing tree that reads the state through `AppContext`.
 * @returns {import("react").JSX.Element} - The context provider wrapping `children`.
 */
const AppProvider = ({ appConfig, children }) => {
    const { pushCampaignConfig, sharpstarApiUrl } = appConfig;
    const [urlParams, setUrlParams] = useState({
        rid: undefined,
        refid1: undefined,
        refid2: undefined,
        refid3: undefined,
        hash: undefined,
    });
    const [phase, setPhase] = useState(''); // 'landing','push','pwa','game' + 'pending' state only when push and pwa can be shown
    const [lpVideos, setLpVideos] = useState(undefined);
    const [offer, setOffer] = useState(undefined);
    const [offerConfigReady, setOfferConfigReady] = useState(false);
    const [leadExtraData, setLeadExtraData] = useState(undefined);
    const [sharpstarService, setSharpstarService] = useState(undefined);
    const [gameConfig, setGameConfig] = useState(undefined);
    const [analyticsConfig, setAnalyticsConfig] = useState(undefined);
    const [adsConfig, setAdsConfig] = useState(undefined);
    const [onesignalId, setOnesignalId] = useState('');

    const hasLeadCreatedRef = useRef(false);

    const { locale } = useContext(LanguageContext);
    const { consentId } = useContext(ConsentContext);

    const isPageLoaded = usePageLoad();
    const botInfo = useCustomDetectBot();

    useEffect(() => {
        const { rid, campaignId, source } = pushCampaignConfig[locale];

        handleUrlParams(rid, campaignId, source);

        const hashChangedHandler = () => {
            const urlHashValue = getPhaseFromHash();

            if (phaseMap[urlHashValue]) {
                updatePhase(phaseMap[urlHashValue]);
            }
        };

        window.addEventListener('hashchange', hashChangedHandler);

        return () => window.removeEventListener('hashchange', hashChangedHandler);
    }, []);

    const createSharpstarService = useCallback(async () => {
        const { default: SharpstarServiceHandler } =
            await import('App/Services/Sharpstar/SharpstarServiceHandler');
        const { source, campaignId } = pushCampaignConfig[locale];

        return new SharpstarServiceHandler(sharpstarApiUrl, source, campaignId);
    }, [sharpstarApiUrl, locale, pushCampaignConfig]);

    useEffect(() => {
        if (typeof botInfo.isBot !== 'boolean') return;

        Sentry.setTag('is_bot', botInfo.isBot ? 'true' : 'false');

        if (botInfo.customIsBot) {
            Sentry.setTag('custom_is_bot', botInfo.customIsBot);
        }
    }, [botInfo.isBot, botInfo.customIsBot]);

    useEffect(() => {
        if (!isPageLoaded) return;

        createSharpstarService().then((sharpstarService) => {
            setSharpstarService(sharpstarService);

            const localCampaign = pushCampaignConfig?.[locale].campaignId;
            const storageCampaign = getStorageValue(STORAGE_KEY, 'app.cmpId');
            const updateStorage =
                !urlParams.hash && (!storageCampaign || storageCampaign !== localCampaign);

            if (updateStorage) {
                setStorageValue(STORAGE_KEY, 'app.cmpId', localCampaign);
            }
        });
    }, [isPageLoaded]);

    useEffect(() => {
        if (!offer) return;

        const { leadData, newGaConfig, newAdValues, newGameConfig, landingVideos } =
            createLpConfigs({
                offer,
                appConfig,
                adDefaultMap,
                gaCustomDimensions,
                defaultVideos: { lp: lpVideo, push: pushVideo, pwa: pwaVideo },
            });

        setLpVideos(landingVideos);
        setAnalyticsConfig(newGaConfig);
        setLeadExtraData(leadData);
        setAdsConfig(newAdValues);
        setGameConfig(newGameConfig);
        setOfferConfigReady(true);
    }, [offer]);

    useEffect(() => {
        if (!onesignalId) return;
        if (hasLeadCreatedRef.current) return;

        hasLeadCreatedRef.current = true;

        if (urlParams.hash) {
            Sentry.withScope((scope) => {
                scope.setLevel('warning');
                scope.setTag('push', 'subscription');
                scope.setTag('osd_id', onesignalId);
                scope.setTag('hash', urlParams.hash);
                scope.setTag('phase', phase);
                Sentry.captureMessage('User attempts push subscription with existing lead hash');
            });
        } else {
            try {
                const requestParams = getLeadExtraData({
                    botInfo,
                    onesignalId,
                    leadExtraData,
                    consentId,
                    storageKey: STORAGE_KEY,
                    urlParams: urlParams,
                    offerRefId: offer?.id,
                });

                sharpstarService.createPushLead(requestParams).then((response) => {
                    setUrlParams((prev) => ({
                        ...prev,
                        hash: response?.hash,
                    }));

                    setMultipleStorageValues(STORAGE_KEY, {
                        'app.hash': response?.hash,
                        'app.cyi': consentId, // stands for consent_id
                    });
                });
            } catch (error) {
                captureCustomException(error, 'CreatePushLead Failed');
            }
        }

        sendOneSignalLeadCount({
            campaignId: pushCampaignConfig?.[locale].campaignId,
            storageKey: STORAGE_KEY,
            sharpstarService,
            onesignalId,
            urlParams,
            botInfo,
        });
    }, [
        phase,
        botInfo,
        urlParams,
        onesignalId,
        leadExtraData,
        consentId,
        offer?.id,
        pushCampaignConfig?.[locale],
    ]);

    const handleUrlParams = (rid, campaignId, source) => {
        const urlParamsObj = getUrlParamObj({
            nestedStorageKey: STORAGE_KEY,
            rid,
        });

        setUrlParams(urlParamsObj);
        Sentry.setTags({
            refid3: urlParamsObj.refid3,
            campaign_id: campaignId,
            ...(source && { source }),
        });
    };

    const updatePhase = (phase) => {
        setPhase(phase);
        if (phase !== 'pending') {
            localStorage.setItem('phase', phase);
        }
    };

    /**
     * Runs only once on app load to set initial phase from localStorage (and hash).
     * - If the current URL has a hash, update to this hash phase and return.
     * - If initial phase is 'landing' → show landing (new user).
     * - If initial phase is not landing and both showPushPhase and showPwaPhase are false → go to game.
     * - Otherwise → set 'pending' and wait for offerConfigReady (handled in App); use updatePhase for any later phase changes.
     */
    const checkInitialPhase = (showPushPhase, showPwaPhase) => {
        const urlHashValue = getPhaseFromHash();
        const phaseFromHash = phaseMap[urlHashValue];

        if (phaseFromHash) {
            updatePhase(phaseFromHash);
            return;
        }

        const initialPhase = localStorage.getItem('phase') || 'landing';

        if (initialPhase === 'landing') {
            updatePhase('landing');
            return;
        }
        if (!showPushPhase && !showPwaPhase) {
            updatePhase('game');
            return;
        }

        updatePhase('pending');
    };

    const contextValue = useMemo(() => {
        return {
            lpVideos,
            botInfo,
            appConfig,
            campaignData: pushCampaignConfig?.[locale],
            sharpstarService,
            phase,
            checkInitialPhase,
            updatePhase,
            setOffer,
            urlParams,
            gameConfig,
            adsConfig,
            analyticsConfig,
            offerConfigReady,
            isPageLoaded,
            setOnesignalId,
        };
    }, [
        lpVideos,
        botInfo,
        appConfig,
        pushCampaignConfig?.[locale],
        sharpstarService,
        phase,
        checkInitialPhase,
        updatePhase,
        setOffer,
        urlParams,
        gameConfig,
        adsConfig,
        analyticsConfig,
        offerConfigReady,
        isPageLoaded,
        setOnesignalId,
    ]);

    return <AppContext value={contextValue}>{children}</AppContext>;
};

export { AppProvider, AppContext };
